import React from 'react';
import styled from 'styled-components';

const WorshipContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
  color: #fff;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;
  background: #fff;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  color: #2c5aa0;
  text-align: center;
  margin-bottom: 50px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const ScheduleTable = styled.div`
  background: #fff;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 60px;
`;

const TableHeader = styled.div`
  background: #2c5aa0;
  color: #fff;
  padding: 25px;
  text-align: center;
  
  h3 {
    font-size: 24px;
    font-weight: 600;
    margin: 0;
  }
`;

const TableContent = styled.div`
  padding: 0;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr;
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const TableHeaderRow = styled(TableRow)`
  background: #f8f9fa;
  font-weight: 600;
  color: #2c5aa0;
`;

const TableCell = styled.div`
  padding: 20px 25px;
  display: flex;
  align-items: center;
  font-size: 16px;
  
  &:not(:last-child) {
    border-right: 1px solid #e9ecef;
    
    @media (max-width: 768px) {
      border-right: none;
      border-bottom: 1px solid #e9ecef;
    }
  }

  @media (max-width: 768px) {
    padding: 15px 25px;
    
    &:before {
      content: attr(data-label);
      font-weight: 600;
      color: #2c5aa0;
      margin-right: 10px;
      min-width: 80px;
    }
  }
`;

const InfoSection = styled.div`
  background: #f8f9fa;
  padding: 50px 40px;
  border-radius: 15px;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const InfoCard = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #2c5aa0;

  h3 {
    font-size: 20px;
    color: #2c5aa0;
    margin-bottom: 20px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  p {
    font-size: 16px;
    color: #555;
    line-height: 1.6;
    margin-bottom: 12px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: #2c5aa0;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const YouTubeSection = styled.div`
  background: #fff;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
  margin-bottom: 40px;

  h3 {
    font-size: 24px;
    color: #2c5aa0;
    margin-bottom: 20px;
    font-weight: 600;
  }

  p {
    font-size: 16px;
    color: #666;
    margin-bottom: 25px;
  }

  .youtube-link {
    display: inline-block;
    background: #ff0000;
    color: #fff;
    padding: 15px 30px;
    border-radius: 25px;
    text-decoration: none;
    font-weight: 600;
    font-size: 16px;
    transition: background 0.3s ease;

    &:hover {
      background: #cc0000;
    }
  }
`;

const BuildingInfo = styled.div`
  background: #fff;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;

  h3 {
    font-size: 24px;
    color: #2c5aa0;
    margin-bottom: 20px;
    font-weight: 600;
    text-align: center;
  }

  p {
    font-size: 16px;
    color: #666;
    text-align: center;
    line-height: 1.6;
  }
`;

const Worship: React.FC = () => {
  return (
    <WorshipContainer>
      <HeroSection>
        <HeroContent>
          <Title>교회 안내</Title>
          <Subtitle>예배 안내 및 교회 위치 정보</Subtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <ContentContainer>
          <SectionTitle>예배 안내</SectionTitle>
          
          <ScheduleTable>
            <TableHeader>
              <h3>예배 시간표</h3>
            </TableHeader>
            <TableContent>
              <TableHeaderRow>
                <TableCell>구분</TableCell>
                <TableCell>시간</TableCell>
                <TableCell>장소</TableCell>
              </TableHeaderRow>
              <TableRow>
                <TableCell data-label="구분:">주일 예배</TableCell>
                <TableCell data-label="시간:">매주 주일 오전 11시</TableCell>
                <TableCell data-label="장소:">대예배실</TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-label="구분:">금요찬양기도집회</TableCell>
                <TableCell data-label="시간:">매주 금요일 오후 7시30분</TableCell>
                <TableCell data-label="장소:">대예배실</TableCell>
              </TableRow>
              <TableRow>
                <TableCell data-label="구분:">주일학교 예배</TableCell>
                <TableCell data-label="시간:">매주 주일 오전 11시</TableCell>
                <TableCell data-label="장소:">소예배실</TableCell>
              </TableRow>
            </TableContent>
          </ScheduleTable>

          <BuildingInfo>
            <h3>교회 건물</h3>
            <p>
              저희 교회는 예배와 교육, 친교를 위한 공간들이 잘 구비되어 있습니다.<br/>
              대예배실과 소예배실, 그리고 다양한 모임을 위한 공간들이 마련되어 있습니다.
            </p>
          </BuildingInfo>

          <YouTubeSection>
            <h3>📺 온라인 예배</h3>
            <p>유튜브를 통해 온라인으로도 예배에 참여하실 수 있습니다.</p>
            <a href="https://www.youtube.com/channel/UC0VeDAbpybpr30PWxhLJwgw" target="_blank" rel="noopener noreferrer" className="youtube-link">
              🎥 얼바인중앙침례교회 유튜브 채널
            </a>
          </YouTubeSection>

          <InfoSection>
            <SectionTitle>연락처 및 위치</SectionTitle>
            <InfoGrid>
              <InfoCard>
                <h3>📞 연락처</h3>
                <p><strong>전화번호:</strong> 949-800-9647</p>
                <p><strong>이메일:</strong> <a href="mailto:ijbcorg@gmail.com">ijbcorg@gmail.com</a></p>
              </InfoCard>

              <InfoCard>
                <h3>📍 교회 위치</h3>
                <p>240 Goddard, Irvine, CA 92618 U.S.A</p>
                <p>주차 공간이 충분히 마련되어 있습니다.</p>
              </InfoCard>

              <InfoCard>
                <h3>🕒 사무실 운영시간</h3>
                <p>월요일 - 금요일: 오전 9시 - 오후 5시</p>
                <p>토요일: 오전 9시 - 오후 2시</p>
                <p>주일: 예배 시간 운영</p>
              </InfoCard>

              <InfoCard>
                <h3>ℹ️ 처음 오시는 분들</h3>
                <p>처음 방문하시는 분들을 위한</p>
                <p>안내 데스크가 준비되어 있습니다.</p>
                <p>언제든 편안하게 문의해 주세요.</p>
              </InfoCard>
            </InfoGrid>
          </InfoSection>
        </ContentContainer>
      </ContentSection>
    </WorshipContainer>
  );
};

export default Worship;